import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, withModifiers as _withModifiers, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "comenzi-container" }
const _hoisted_2 = { class: "tabs-row" }
const _hoisted_3 = { class: "tab-label" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "tab-content" }
const _hoisted_6 = { class: "filters-bar" }
const _hoisted_7 = {
  key: 0,
  class: "tab-content"
}

import { ref, onMounted, nextTick } from 'vue';
import { useRoute, onBeforeRouteLeave } from 'vue-router';
import DxTabs from 'devextreme-vue/tabs';
import { custom } from 'devextreme/ui/dialog';
import FiltreComenzi from './FilterComenzi.vue';
import GridComenzi from './GridComenzi.vue';
import GridComandaDetalii from './GridComandaDetalii.vue';

interface Filter {
  category: number | null;
  start: string;
  end: string;
  produsId: string | null;
}

interface OrderData {
  Id: string | number;
  Cod: string;
  [key: string]: any;
}

interface OpenOrderTab {
  orderId: string;
  orderData: OrderData;
  isNew?: boolean;
  remountNonce: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PRO_ComenziView',
  setup(__props) {

const route = useRoute();

const filters = ref<Filter>({ category: null, start: '', end: '', produsId: null });
const selectedIndex = ref(0);
const openOrders = ref<OpenOrderTab[]>([]);
const orderTabs = ref<{ text: string; orderId: string }[]>([]);

// Setat pe durata schimbărilor de tab pornite din cod (după ce am confirmat/salvat),
// ca să nu retrigger-uim propriile noastre actualizări de selectedIndex.
let suppressTabGuard = false;

// Setat cât timp confirmSwitchAway verifică/tratează comanda părăsită — transmis ca prop
// către GridComandaDetalii, ca auto-salvarea ei proprie (pe focusout) să nu intervină
// concurent peste dialogul Salvează/Renunță/Rămân.
const suppressChildAutoSave = ref(false);

// ── Tracking modificari nesalvate per tab ──
const unsavedOrderIds = ref<Set<string>>(new Set());
const gridRefs = ref<Map<string, any>>(new Map());

function setGridRef(orderId: string, el: any) {
  if (el) gridRefs.value.set(orderId, el);
  else gridRefs.value.delete(orderId);
}

// Bug cunoscut devextreme-vue: prima montare a unui GridComandaDetalii dintr-o sesiune de
// navigare poate eșua să inițializeze widget-ul intern al gridului de linii (motiv neclar,
// posibil coliziune internă la construirea simultană a mai multor DxDataGrid din același
// component) — orice montare ulterioară (a doua comandă, sau reintrarea pe aceeași) funcționează
// mereu corect. Fix: dacă respectiva componentă raportează eșecul, o remontăm o singură dată,
// automat, schimbând key-ul ei — echivalentul lui "ieși și reintri", dar invizibil pentru user.
function onGridInitFailed(orderId: string) {
  const order = openOrders.value.find(o => o.orderId === orderId);
  if (!order || order.remountNonce > 0) return; // o singură reîncercare, ca să nu bucleze
  order.remountNonce++;
}

function onUnsavedChange(orderId: string, has: boolean) {
  const next = new Set(unsavedOrderIds.value);
  has ? next.add(orderId) : next.delete(orderId);
  unsavedOrderIds.value = next;
}

function hasAnyUnsaved(): boolean {
  return unsavedOrderIds.value.size > 0;
}

async function confirmLeave(): Promise<boolean> {
  return custom({
    title: 'Modificări nesalvate',
    messageHtml: 'Aveți modificări nesalvate în comenzi deschise. Sigur doriți să continuați?',
    buttons: [
      { text: 'Rămân', stylingMode: 'outlined', onClick: () => false },
      { text: 'Părăsesc', type: 'danger', onClick: () => true },
    ],
  }).show();
}

onBeforeRouteLeave(async (_to, _from, next) => {
  if (!hasAnyUnsaved()) { next(); return; }
  const ok = await confirmLeave();
  ok ? next() : next(false);
});

function currentOpenOrderId(): string | null {
  if (selectedIndex.value === 0) return null;
  return openOrders.value[selectedIndex.value - 1]?.orderId ?? null;
}

// Verifică/salvează linia needitată a comenzii PĂRĂSITE, ÎNAINTE de a comuta efectiv
// tab-ul (deci înainte ca v-if să demonteze componenta ei — o dată demontată, datele
// nesalvate din grid se pierd ireversibil, deci nu are rost să verificăm după).
// Dialogul apare de fiecare dată când există o linie needitată — nu doar dacă salvarea
// automată eșuează — ca userul să decidă explicit, nu să se întâmple ceva pe tăcute.
async function confirmSwitchAway(): Promise<boolean> {
  const orderId = currentOpenOrderId();
  if (!orderId) return true;

  const gridComp = gridRefs.value.get(orderId);
  if (!gridComp?.hasPendingChanges?.()) return true;

  const cod = orderTabs.value.find(t => t.orderId === orderId)?.text ?? '';
  const choice: 'save' | 'discard' | 'stay' = await custom({
    title: 'Linie nesalvată',
    messageHtml: `<div style="padding:4px 0">Comanda <b>${cod}</b> are un produs pe linie care nu a fost salvat.<br><br>Ce vrei să faci?</div>`,
    buttons: [
      { text: 'Salvează', type: 'default', onClick: () => 'save' },
      { text: 'Renunță la linie', type: 'danger', stylingMode: 'outlined', onClick: () => 'discard' },
      { text: 'Rămân pe comandă', stylingMode: 'outlined', onClick: () => 'stay' },
    ],
  }).show();

  if (choice === 'stay') return false;

  if (choice === 'discard') {
    gridComp.discardUnsavedLine?.();
    return true;
  }

  // 'save' — încearcă efectiv salvarea; dacă eșuează (ex. produs lipsă), arată din nou
  // dialogul, ca userul să aleagă altceva (renunță sau rămâne să corecteze).
  const { ok } = await gridComp.trySaveBeforeLeave();
  if (ok) return true;
  return confirmSwitchAway();
}

let tabSwitchInProgress = false;

async function onTabChanged(e: any) {
  // DxTabs declanșează "option-changed" pentru mai multe opțiuni interne la o singură
  // selecție (nu doar "selectedIndex") — fără filtrul ăsta, handler-ul rula de mai multe
  // ori per click, arătând dialogul de confirmare de 2-3 ori.
  if (e.name !== 'selectedIndex') return;
  if (suppressTabGuard || tabSwitchInProgress) return;

  const internalIndex = e.component.option('selectedIndex');
  if (internalIndex == null || internalIndex < 0) return;
  const targetIndex = internalIndex + 1;
  if (targetIndex === selectedIndex.value) return;

  const previousInternalIndex = selectedIndex.value - 1;
  tabSwitchInProgress = true;
  suppressChildAutoSave.value = true;
  try {
    const ok = await confirmSwitchAway();

    if (!ok) {
      suppressTabGuard = true;
      e.component.option('selectedIndex', previousInternalIndex);
      await nextTick();
      suppressTabGuard = false;
      return;
    }

    selectedIndex.value = targetIndex;
  } finally {
    tabSwitchInProgress = false;
    suppressChildAutoSave.value = false;
  }
}

async function goToListTab() {
  if (selectedIndex.value === 0) return;
  suppressChildAutoSave.value = true;
  try {
    const ok = await confirmSwitchAway();
    if (!ok) return;
    selectedIndex.value = 0;
  } finally {
    suppressChildAutoSave.value = false;
  }
}

function updateFilters(newFilters: Filter) {
  filters.value = newFilters;
  selectedIndex.value = 0;
}

function openOrder(order: OrderData) {
  const comandaIdString = String(order.Id);

  const existingIndex = openOrders.value.findIndex(t => t.orderId === comandaIdString);
  if (existingIndex !== -1) {
    selectedIndex.value = existingIndex + 1;
    return;
  }

  openOrders.value.push({ orderId: comandaIdString, orderData: order, isNew: !!order._isNew, remountNonce: 0 });
  orderTabs.value = [...orderTabs.value, { text: order.Cod, orderId: comandaIdString }];
  selectedIndex.value = orderTabs.value.length;
}

async function closeTab(index: number) {
  const order = openOrders.value[index];
  if (order && unsavedOrderIds.value.has(order.orderId)) {
    const ok = await custom({
      title: 'Modificări nesalvate',
      messageHtml: `Comanda <b>${orderTabs.value[index]?.text ?? ''}</b> are modificări nesalvate. Închizi oricum?`,
      buttons: [
        { text: 'Rămân', stylingMode: 'outlined', onClick: () => false },
        { text: 'Închide fără salvare', type: 'danger', onClick: () => true },
      ],
    }).show();
    if (!ok) return;
  }

  if (order) {
    unsavedOrderIds.value.delete(order.orderId);
    gridRefs.value.delete(order.orderId);
  }

  const wasActiveTab = selectedIndex.value === index + 1;
  const activeOrderId = wasActiveTab ? null : openOrders.value[selectedIndex.value - 1]?.orderId;

  openOrders.value.splice(index, 1);
  orderTabs.value = orderTabs.value.filter((_, i) => i !== index);

  if (wasActiveTab) {
    // Închizi comanda pe care o vizualizai — te întorci mereu pe gridul de comenzi
    selectedIndex.value = 0;
  } else {
    // Închizi un tab din fundal — rămâi pe comanda curentă, doar recalculezi indexul
    const newIndex = activeOrderId
      ? openOrders.value.findIndex(o => o.orderId === activeOrderId) + 1
      : 0;
    selectedIndex.value = newIndex;
  }
}

onMounted(() => {
  const comandaIdFromRoute = route.params.comandaId as string;
  if (comandaIdFromRoute) {
    openOrder({
      Id: comandaIdFromRoute,
      Cod: comandaIdFromRoute,
      Nume: 'Încărcare...',
      DataComenzii: '',
      ValoareComanda: 0,
    });
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", {
        class: _normalizeClass(["tab-fixed", { 'tab-fixed-active': selectedIndex.value === 0 }]),
        onClick: goToListTab
      }, [...(_cache[0] || (_cache[0] = [
        _createElementVNode("span", null, "Comenzi", -1)
      ]))], 2),
      (orderTabs.value.length > 0)
        ? (_openBlock(), _createBlock(_unref(DxTabs), {
            key: 0,
            class: "tabs-scroll",
            "data-source": orderTabs.value,
            "selected-index": selectedIndex.value - 1,
            "item-template": "tabItem",
            "scrolling-enabled": true,
            "show-nav-buttons": true,
            onOptionChanged: onTabChanged
          }, {
            tabItem: _withCtx(({ data, index }) => [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("span", null, _toDisplayString(data.text), 1),
                _createElementVNode("span", {
                  class: "tab-close",
                  onClick: _withModifiers(($event: any) => (closeTab(index)), ["stop"])
                }, "×", 8, _hoisted_4)
              ])
            ]),
            _: 1
          }, 8, ["data-source", "selected-index"]))
        : _createCommentVNode("", true)
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createVNode(FiltreComenzi, { onFiltersChanged: updateFilters })
      ]),
      _createVNode(GridComenzi, {
        filters: filters.value,
        onOpenOrder: openOrder,
        class: "grid-fill"
      }, null, 8, ["filters"])
    ], 512), [
      [_vShow, selectedIndex.value === 0]
    ]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(openOrders.value, (order, i) => {
      return (_openBlock(), _createElementBlock(_Fragment, {
        key: order.orderId
      }, [
        (selectedIndex.value === i + 1)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              (_openBlock(), _createBlock(GridComandaDetalii, {
                key: order.orderId + '_' + order.remountNonce,
                ref_for: true,
                ref: (el) => setGridRef(order.orderId, el),
                comandaId: order.orderId,
                orderData: order.orderData,
                "is-new": order.isNew,
                "suppress-auto-save": suppressChildAutoSave.value,
                class: "fill",
                onOpenOrder: openOrder,
                onUnsavedChangesChange: (has) => onUnsavedChange(order.orderId, has),
                onGridInitFailed: ($event: any) => (onGridInitFailed(order.orderId))
              }, null, 8, ["comandaId", "orderData", "is-new", "suppress-auto-save", "onUnsavedChangesChange", "onGridInitFailed"]))
            ]))
          : _createCommentVNode("", true)
      ], 64))
    }), 128))
  ]))
}
}

})